import {FeedAggregatorDataProvider} from './editor';
import {stripHtml} from '../utils/dom';
import {IPR_CATEGORY_MAP} from './iprCategories';
import axios from 'axios';
import Typesense from 'typesense';

/**
 * Builds card object.
 *
 * @param {String} image
 * @param {String} title
 * @param {String} date
 * @param {String} excerpt
 * @param {String} link
 * @param {Number|String} timestamp
 * @returns {{date, image, link, excerpt, title}}
 */
const buildCardItem = (image, title, date, excerpt, link, timestamp) => {
  let id = generateUniqueId();
  return {id, image, title, date, excerpt, link, timestamp: parseInt(timestamp)}
};

/**
 * Get image from post object.
 *
 * @param {Object} post
 * @returns {*|string}
 */
const getImage = (post) => {
  if (!post || !post.hasOwnProperty('wp:featuredmedia')) {
    return null;
  }

  const featuredMedia = post['wp:featuredmedia'].shift();
  if (!featuredMedia || !featuredMedia['source_url']) {
    return null;
  }

  let image = featuredMedia['source_url'];

  // Try to load smaller images.
  let imageSizes = featuredMedia.media_details?.sizes?.['medium_large'];
  if (imageSizes && imageSizes.hasOwnProperty('source_url')) {
    image = imageSizes['source_url'];
  }

  return image;
};

function generateUniqueId() {
  const timestamp = Date.now().toString(36);
  const randomStr = Math.random().toString(36).substr(2, 5);
  return timestamp + randomStr;
}

function formatDate(date) {
  const month = date.toLocaleString('en-US', {month: 'long'});
  const day = date.getDate();
  const year = date.getFullYear();
  return Number.isNaN(date.getTime()) ? '' : `${month} ${day}, ${year}`;
}

const normalizeWpApiResponse = (response) => {
  return response.map(item => {
    const {title, link, excerpt, _embedded, date} = item;
    let postTimestamp = parseInt((new Date(date).getTime() / 1000).toFixed(0));
    let postDate = new Date(date);

    const image = getImage(_embedded);
    return buildCardItem(image, title['rendered'], formatDate(postDate), excerpt['rendered'], link, postTimestamp);
  });
};

const normalizeTypesenseResponse = (response) => {
  return response.map(item => {
    const {title, thumbnail, excerpt, url, published_at} = item.document;
    const date = new Date(published_at * 1000);
    return buildCardItem(thumbnail, title, formatDate(date), excerpt, url, published_at);
  });
};

const get = async (url, queryParams) => {
  const apiClientInstance = axios.create({timeout: 5000});
  const {data} = await apiClientInstance.request({method: 'get', url, params: queryParams});

  return data ? data: [];
};

const MEDIA_NS = 'http://search.yahoo.com/mrss/';

function getImageFromRssItem(itemEl) {
  const mediaContent = itemEl.getElementsByTagNameNS(MEDIA_NS, 'content')[0];
  if (mediaContent && mediaContent.getAttribute('url')) {
    return mediaContent.getAttribute('url');
  }
  const enclosure = itemEl.querySelector('enclosure');
  if (enclosure && enclosure.getAttribute('url')) {
    return enclosure.getAttribute('url');
  }
  return null;
}

async function fetchSingleIprFeed(url, count) {
  const client = axios.create({timeout: 5000, responseType: 'text'});
  const {data} = await client.request({method: 'get', url});

  const doc = new DOMParser().parseFromString(data, 'text/xml');
  if (doc.querySelector('parsererror')) {
    return [];
  }
  const items = doc.querySelectorAll('channel > item');
  const result = [];

  const limit = Math.min(items.length, count);
  for (let i = 0; i < limit; i++) {
    const item = items[i];
    result.push({
      title: item.querySelector('title')?.textContent?.trim() || '',
      link: item.querySelector('link')?.textContent?.trim() || '',
      pubDate: item.querySelector('pubDate')?.textContent?.trim() || '',
      description: item.querySelector('description')?.textContent?.trim() || '',
      imageUrl: getImageFromRssItem(item),
    });
  }
  return result;
}

function resolveIprUrls(categories) {
  return categories
    .map(key => IPR_CATEGORY_MAP[key]?.url)
    .filter(Boolean);
}

async function queryIprFeed(source, count) {
  const {categories} = source;
  if (!categories || categories.length === 0) return [];

  const urls = resolveIprUrls(categories);
  if (urls.length === 0) return [];

  try {
    const results = await Promise.all(urls.map(url => fetchSingleIprFeed(url, count)));
    const merged = results.flat().sort((a, b) => new Date(b.pubDate) - new Date(a.pubDate));

    const seen = new Set();
    return merged.filter(item => {
      if (seen.has(item.link)) return false;
      seen.add(item.link);
      return true;
    });
  } catch (err) {
    console.error('iPR feed fetch failed:', err);
    return [];
  }
}

const normalizeIprResponse = (response) => {
  return response.map(item => {
    const pubDate = new Date(item.pubDate);
    const rawTimestamp = Math.floor(pubDate.getTime() / 1000);
    const timestamp = Number.isFinite(rawTimestamp) ? rawTimestamp : 0;
    const excerpt = stripHtml(item.description) || item.description || '';

    return buildCardItem(item.imageUrl, item.title, formatDate(pubDate), excerpt, item.link, timestamp);
  });
};

async function queryWpApi(source, count) {
  const {host, categoryIds, tagIds} = source;
  const url = `${host}/wp-json/wp/v2/posts`;

  let queryParams = {
    '_embed': true,
    per_page: count, // fetch `count` per feed so we don't need to make additional pagination requests
  };

  if (Array.isArray(categoryIds) && categoryIds.length > 0) {
    queryParams['categories'] = categoryIds.join(',');
  }

  if (Array.isArray(tagIds) && tagIds.length > 0) {
    queryParams['tags'] = tagIds.join(',');
  }

  return await get(url, queryParams);
}

function typesenseConnectionData() {
  const data = {host: '', key: ''};

  for (const key in data) {
    const element = document.querySelector(`meta[name="typesense-${key}"]`);
    if (element) {
      data[key] = element.getAttribute('content');
    }
  }

  return data;
}

async function queryTypesenseApi(source, count) {
  const {collection, filter_by, count: limit} = source;
  const {host, key} = typesenseConnectionData();

  if (!host || !key) {
    throw new Error('Unable to connect to the search engine');
  }

  const client = new Typesense.Client({
    'nodes': [
      {
        'host': host,
        'port': '443',
        'protocol': 'https'
      }
    ],
    'apiKey': key,
  });

  let searchParameters = {
    'q': '*',
    'query_by': 'title',
    'filter_by': filter_by,
    'sort_by': 'published_at:desc',
    'per_page': limit,
  };

  const result = await client.collections(collection).documents().search(searchParameters);
  const {hits} = result;
  if (!hits) {
    return [];
  }

  return hits;
}

export const loadDataFromSource = async (source, count) => {
  let data = [];
  const {dataProvider: provider} = source;

  switch (provider) {
    case FeedAggregatorDataProvider.wordpressApi:
      data = await queryWpApi(source, count);
      break;
    case FeedAggregatorDataProvider.typesense:
      data = await queryTypesenseApi(source, count);
      break;
    case FeedAggregatorDataProvider.ipr:
      data = await queryIprFeed(source, count);
      break;
  }

  return data;
};

export const normalizeResponseData = async (rawData, provider) => {
  let normalized = [];

  switch (provider) {
    case FeedAggregatorDataProvider.wordpressApi:
      normalized = normalizeWpApiResponse(rawData);
      break;
    case FeedAggregatorDataProvider.typesense:
      normalized = normalizeTypesenseResponse(rawData);
      break;
    case FeedAggregatorDataProvider.ipr:
      normalized = normalizeIprResponse(rawData);
      break;
  }

  return normalized;
}
